import { useMemo } from 'react';

import { usePlayerStats } from '@hooks/usePlayerStats';
import type {
  PlayerStatsAggregation,
  PlayerStatsPosition,
  PlayerStatsVenue
} from '@api-service/player-stats';
import { PERCENTILE_MINIMUM_MINUTES } from '@lib/page-utils/player-profile/metrics';
import {
  buildPopulations,
  createPercentileLookup,
  type PercentileLookup
} from '@lib/page-utils/player-profile/percentiles';

/**
 * The stats endpoint caps `limit` at 1000 and there are fewer than 800 players
 * in the league, so one request returns the entire qualifying population for a
 * position and the percentile is a client-side sort. No backend percentile
 * field is needed.
 */
const WHOLE_POPULATION_LIMIT = 1000;

export type UsePositionPercentilesOptions = {
  position: PlayerStatsPosition | null;
  /** Comma-separated field groups, matching the stats endpoint's `fields`. */
  fields: string;
  statPaths: string[];
  /**
   * The profiled player's own stats row id, excluded from the comparison
   * population. A player cannot strictly beat himself, so leaving the row in
   * caps the league leader at N-1 of N; excluding it makes the leader read
   * 100th. It also removes a float trap: the profile row and the population
   * row arrive on separate responses whose sums can differ in the last bits,
   * which the strict comparison would otherwise turn into a wrong rank.
   */
  excludeRowId?: string | null;
  aggregation: PlayerStatsAggregation;
  venue: PlayerStatsVenue;
  minGameweek: number;
  maxGameweek: number;
  season: number | null;
  accessToken?: string;
  enabled?: boolean;
};

export type UsePositionPercentilesResult = {
  getPercentile: PercentileLookup;
  /** How many other players cleared the minutes cut-off, for the "of N" copy. */
  populationSize: number;
  isLoading: boolean;
};

export const usePositionPercentiles = ({
  position,
  fields,
  statPaths,
  excludeRowId = null,
  aggregation,
  venue,
  minGameweek,
  maxGameweek,
  season,
  accessToken,
  enabled = true
}: UsePositionPercentilesOptions): UsePositionPercentilesResult => {
  const isEnabled = enabled && position !== null;

  const { data, isLoading } = usePlayerStats({
    fields,
    aggregation,
    venue,
    minGameweek,
    maxGameweek,
    minMinutes: PERCENTILE_MINIMUM_MINUTES,
    limit: WHOLE_POPULATION_LIMIT,
    enabled: isEnabled,
    ...(position !== null ? { position } : {}),
    ...(season !== null ? { season } : {}),
    ...(accessToken ? { accessToken } : {})
  });

  const rows = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.data) ?? []).filter(
        (row) => row.id !== excludeRowId
      ),
    [data, excludeRowId]
  );

  const getPercentile = useMemo(
    () => createPercentileLookup(buildPopulations(rows, statPaths)),
    // statPaths is a stable module-level array per position, so joining it
    // keeps the dependency comparison by value rather than by reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, statPaths.join(',')]
  );

  return {
    getPercentile,
    populationSize: rows.length,
    isLoading: isEnabled && isLoading
  };
};
